import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';

const ShareConfigContext = createContext(null);

/**
 * Provides platform-wide share configuration.
 * Fetches once from /api/public/share-config and caches it.
 *
 * All URL generation (share, QR, embed, deep-links, checkout success) should use
 * `buildShareUrl(path)` from this context to respect the parent-domain configuration.
 */
export const ShareConfigProvider = ({ children }) => {
    const [config, setConfig] = useState({
        public_share_url: null,
        embed_mode: 'none',
        parent_origin: null,
        allowed_origins: [],
        loaded: false,
    });

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get('/public/share-config');
                if (mounted) setConfig({ ...data, loaded: true });
            } catch (_) {
                if (mounted) setConfig(c => ({ ...c, loaded: true }));
            }
        })();
        return () => { mounted = false; };
    }, []);

    const value = useMemo(() => {
        const base = (config.public_share_url || '').replace(/\/$/, '');
        const buildShareUrl = (path) => {
            if (!path) return base || (typeof window !== 'undefined' ? window.location.origin : '');
            const clean = path.startsWith('/') ? path : `/${path}`;
            if (base) return `${base}${clean}`;
            if (typeof window !== 'undefined') return `${window.location.origin}${clean}`;
            return clean;
        };
        const isEmbedded = typeof window !== 'undefined' && window.self !== window.top;
        return { ...config, buildShareUrl, isEmbedded };
    }, [config]);

    return <ShareConfigContext.Provider value={value}>{children}</ShareConfigContext.Provider>;
};

export const useShareConfig = () => {
    const ctx = useContext(ShareConfigContext);
    if (!ctx) {
        // fallback for hooks used before provider is mounted
        return {
            public_share_url: null,
            embed_mode: 'none',
            parent_origin: null,
            allowed_origins: [],
            loaded: false,
            isEmbedded: false,
            buildShareUrl: (path) => (typeof window !== 'undefined' ? `${window.location.origin}${path?.startsWith('/') ? path : `/${path || ''}`}` : path || ''),
        };
    }
    return ctx;
};
